import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Form, Badge, Alert, InputGroup, Modal } from 'react-bootstrap';
import { 
  FaSearch, FaIdCard, FaFilter, FaTimes,
  FaUsers, FaUserTie, FaBuilding, FaHardHat, FaCalendarAlt,
  FaInfoCircle, FaEye, FaHistory, FaUser, FaEnvelope, FaPhone,
  FaMapMarkerAlt, FaClock, FaCheckCircle, FaTimesCircle,
  FaEdit, FaSpinner
} from 'react-icons/fa';
import supabase from '../../supabaseClient'; // Importando o supabase client

const Acessos = () => {
  // Estados para filtros
  const [filters, setFilters] = useState({
    tipo: '',
    expositor: '',
    evento: '',
    empresaMontadora: '',
    statusEntrada: '',
    search: ''
  });
  
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [todasPessoas, setTodasPessoas] = useState([]);
  const [expositores, setExpositores] = useState([]);
  const [empresasMontadoras, setEmpresasMontadoras] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [accessHistory, setAccessHistory] = useState([]);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  // Efeito para carregar dados iniciais
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Função para carregar dados iniciais
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      // Carregar empresas montadoras
      const { data: montadorasData, error: montadorasError } = await supabase
        .from('caex_empresas_montadoras')
        .select('*')
        .order('nome');
      
      if (montadorasError) throw montadorasError;
      setEmpresasMontadoras(montadorasData || []);
      
      // Carregar expositores
      const { data: expositoresData, error: expositoresError } = await supabase
        .from('caex_expositores')
        .select('*')
        .order('nome_empresa');
      
      if (expositoresError) throw expositoresError;
      setExpositores(expositoresData || []);
      
      // Carregar registros de acesso para verificar quem está presente
      const { data: registrosData, error: registrosError } = await supabase
        .from('caex_registros_acesso')
        .select('*')
        .is('data_saida', null);
      
      if (registrosError) throw registrosError;
      
      // Carregar montadores
      const { data: montadoresData, error: montadoresError } = await supabase
        .from('caex_montadores')
        .select(`
          *,
          caex_empresas_montadoras (
            id,
            nome
          )
        `);
      
      if (montadoresError) throw montadoresError;
      
      // Carregar credenciais
      const { data: credenciaisData, error: credenciaisError } = await supabase
        .from('caex_credenciais')
        .select(`
          *,
          caex_expositores (
            id,
            nome_empresa
          )
        `);
      
      if (credenciaisError) throw credenciaisError;
      
      // Formatar montadores
      const montadoresFormatados = montadoresData.map(montador => {
        // Verificar se o montador está presente
        const entradaAtiva = registrosData.find(
          registro => registro.montador_id === montador.id && registro.data_saida === null
        );
        
        return {
          id: montador.id,
          nome: montador.nome,
          cpf: montador.cpf,
          tipo: 'montador',
          empresaMontadora: montador.caex_empresas_montadoras?.nome || 'Sem empresa',
          empresaMontadoraId: montador.empresa_montadora_id,
          cargo: montador.cargo,
          telefone: montador.telefone,
          email: montador.email,
          dataInicio: montador.data_inicio,
          dataFim: montador.data_fim,
          horarioInicio: montador.horario_inicio,
          horarioFim: montador.horario_fim,
          status: montador.status,
          statusEntrada: entradaAtiva ? 'registrado' : 'pendente',
          dataUltimaEntrada: entradaAtiva ? entradaAtiva.data_entrada : null,
          expositorAssociado: 'Vários', // Por padrão, um montador pode trabalhar para vários expositores
          pulseira: entradaAtiva?.pulseira_numero
        };
      });
      
      // Formatar credenciais
      const credenciaisFormatadas = credenciaisData.map(credencial => {
        // Verificar se a credencial está presente
        const entradaAtiva = registrosData.find(
          registro => registro.credencial_id === credencial.id && registro.data_saida === null
        );
        
        // Obter nome do expositor
        const expositorNome = credencial.caex_expositores?.nome_empresa || 'Sem expositor';
        
        return {
          id: credencial.id,
          nome: credencial.nome,
          cpf: credencial.cpf,
          tipo: 'credencial',
          tipoCredencial: credencial.tipo_credencial,
          empresa: credencial.empresa,
          cargo: credencial.cargo,
          telefone: credencial.telefone,
          email: credencial.email,
          dataInicio: credencial.data_inicio,
          dataFim: credencial.data_fim,
          status: credencial.status,
          statusEntrada: entradaAtiva ? 'registrado' : 'pendente',
          dataUltimaEntrada: entradaAtiva ? entradaAtiva.data_entrada : null,
          expositorAssociado: expositorNome,
          expositorId: credencial.expositor_id,
          pulseira: entradaAtiva?.pulseira_numero
        };
      });
      
      // Combinar montadores e credenciais
      setTodasPessoas([...montadoresFormatados, ...credenciaisFormatadas]);
      
      // Definir lista de eventos (como não temos uma tabela específica, vamos usar valores fixos)
      setEventos([
        { id: 1, nome: 'Congresso Nacional de Medicina 2025' },
        { id: 2, nome: 'Feira de Tecnologia MedTech 2025' },
        { id: 3, nome: 'Simpósio de Inovação em Saúde' },
        { id: 4, nome: 'Expo Hospital & Health' }
      ]);
      
    } catch (error) {
      console.error('Erro ao carregar dados iniciais:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para buscar histórico de acesso de uma pessoa
  const fetchAccessHistory = async (person) => {
    try {
      setLoading(true);
      
      let query = supabase.from('caex_registros_acesso').select('*');
      
      if (person.tipo === 'montador') {
        query = query.eq('montador_id', person.id);
      } else {
        query = query.eq('credencial_id', person.id);
      }
      
      query = query.order('data_entrada', { ascending: false });
      
      const { data, error } = await query;
      
      if (error) throw error;
      
      setAccessHistory(data || []);
      setShowHistoryModal(true);
      
    } catch (error) {
      console.error('Erro ao buscar histórico de acesso:', error);
    } finally {
      setLoading(false);
    }
  };

  // Função para aplicar filtros
  const filteredPessoas = todasPessoas.filter(pessoa => {
    // Filtro por tipo
    if (filters.tipo && pessoa.tipo !== filters.tipo) {
      return false;
    }

    // Filtro por expositor
    if (filters.expositor && filters.expositor !== '') {
      if (pessoa.expositorId !== parseInt(filters.expositor) && pessoa.expositorAssociado !== expositores.find(e => e.id === parseInt(filters.expositor))?.nome_empresa) {
        return false;
      }
    }

    // Filtro por empresa montadora (apenas para montadores)
    if (filters.empresaMontadora && pessoa.tipo === 'montador') {
      if (pessoa.empresaMontadoraId !== parseInt(filters.empresaMontadora)) {
        return false;
      }
    }

    // Filtro por status de entrada
    if (filters.statusEntrada && pessoa.statusEntrada !== filters.statusEntrada) {
      return false;
    }

    // Filtro por evento
    if (filters.evento && filters.evento !== '') {
      // Como não temos um campo de evento na tabela, estamos considerando que
      // todos estão associados ao evento selecionado para simplificar
      return true;
    }

    // Filtro de busca
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      return pessoa.nome.toLowerCase().includes(searchTerm) || 
             (pessoa.cpf && pessoa.cpf.includes(searchTerm)) ||
             (pessoa.empresa && pessoa.empresa.toLowerCase().includes(searchTerm)) ||
             (pessoa.email && pessoa.email.toLowerCase().includes(searchTerm)) ||
             (pessoa.expositorAssociado && pessoa.expositorAssociado.toLowerCase().includes(searchTerm)) ||
             (pessoa.empresaMontadora && pessoa.empresaMontadora.toLowerCase().includes(searchTerm));
    }

    return true;
  });

  // Handlers
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const resetFilters = () => {
    setFilters({
      tipo: '',
      expositor: '',
      evento: '',
      empresaMontadora: '',
      statusEntrada: '',
      search: ''
    });
  };

  // Handler para abrir modal de detalhes
  const handleViewDetails = (pessoa) => {
    setSelectedPerson(pessoa);
    setShowDetailsModal(true);
  };

  // Handler para abrir modal de histórico
  const handleViewHistory = (pessoa) => {
    setSelectedPerson(pessoa);
    fetchAccessHistory(pessoa);
  };

  // Handler para fechar modal de detalhes
  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedPerson(null);
  };

  // Handler para fechar modal de histórico
  const handleCloseHistory = () => {
    setShowHistoryModal(false);
    setAccessHistory([]);
  };

  // Funções de renderização
  const renderStatusBadge = (status) => {
    switch (status) {
      case 'ativo':
        return <Badge bg="success" className="modern-badge">Ativo</Badge>;
      case 'inativo':
        return <Badge bg="danger" className="modern-badge">Inativo</Badge>;
      default:
        return <Badge bg="secondary" className="modern-badge">Indefinido</Badge>;
    }
  };

  const renderTipoBadge = (tipo, tipoCredencial) => {
    if (tipo === 'credencial' && tipoCredencial) {
      switch (tipoCredencial.toLowerCase()) {
        case 'expositor':
          return <Badge bg="primary" className="modern-badge">Expositor</Badge>;
        case 'staff':
          return <Badge bg="success" className="modern-badge">Staff</Badge>;
        case 'imprensa':
          return <Badge bg="info" className="modern-badge">Imprensa</Badge>;
        case 'vip':
          return <Badge bg="danger" className="modern-badge">VIP</Badge>;
        default:
          return <Badge bg="primary" className="modern-badge">{tipoCredencial}</Badge>;
      }
    }
    
    switch (tipo) {
      case 'expositor':
        return <Badge bg="primary" className="modern-badge">Expositor</Badge>;
      case 'montador':
        return <Badge bg="secondary" className="modern-badge">Montador</Badge>;
      case 'credencial':
        return <Badge bg="info" className="modern-badge">Credencial</Badge>;
      default:
        return <Badge bg="light" className="modern-badge">Outro</Badge>;
    }
  };

  const renderStatusEntradaBadge = (statusEntrada) => {
    switch (statusEntrada) {
      case 'registrado':
        return <Badge bg="success" className="modern-badge">Registrado</Badge>;
      case 'pendente':
        return <Badge bg="warning" className="modern-badge">Pendente</Badge>;
      default:
        return <Badge bg="secondary" className="modern-badge">Indefinido</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    return date.toLocaleString('pt-BR');
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaUsers className="dashboard-title-icon" />
              Credenciais
            </h1>
            <p className="dashboard-subtitle">
              Visualize e gerencie todas as pessoas que prestam serviço nos eventos
            </p>
          </div>
        </div>

        {/* Área de filtros */}
        {showFilters && (
          <Card className="modern-table-card mb-4">
            <Card.Body className="modern-card-body">
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Tipo de Pessoa</Form.Label>
                    <Form.Select
                      name="tipo"
                      value={filters.tipo}
                      onChange={handleFilterChange}
                    >
                      <option value="">Todos os tipos</option>
                      <option value="credencial">Credencial</option>
                      <option value="montador">Montador</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Expositor</Form.Label>
                    <Form.Select
                      name="expositor"
                      value={filters.expositor}
                      onChange={handleFilterChange}
                    >
                      <option value="">Todos os expositores</option>
                      {expositores.map(expositor => (
                        <option key={expositor.id} value={expositor.id}>
                          {expositor.nome_empresa}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Empresa Montadora</Form.Label>
                    <Form.Select
                      name="empresaMontadora"
                      value={filters.empresaMontadora}
                      onChange={handleFilterChange}
                      disabled={filters.tipo === 'credencial'}
                    >
                      <option value="">Todas as empresas</option>
                      {empresasMontadoras.map(empresa => (
                        <option key={empresa.id} value={empresa.id}>
                          {empresa.nome}
                        </option>
                      ))}
                    </Form.Select>
                    {filters.tipo === 'credencial' && (
                      <Form.Text className="text-muted">
                        Disponível apenas para montadores
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status de Entrada</Form.Label>
                    <Form.Select
                      name="statusEntrada"
                      value={filters.statusEntrada}
                      onChange={handleFilterChange}
                    >
                      <option value="">Todos os status</option>
                      <option value="registrado">Registrado</option>
                      <option value="pendente">Pendente</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              
              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Evento</Form.Label>
                    <Form.Select
                      name="evento"
                      value={filters.evento}
                      onChange={handleFilterChange}
                    >
                      <option value="">Todos os eventos</option>
                      {eventos.map(evento => (
                        <option key={evento.id} value={evento.id}>
                          {evento.nome}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Buscar</Form.Label>
                    <div className="position-relative">
                      <Form.Control 
                        type="text" 
                        placeholder="Nome, CPF, CNPJ, expositor, empresa..."
                        name="search"
                        value={filters.search}
                        onChange={handleFilterChange}
                      />
                      <FaSearch className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted" />
                    </div>
                  </Form.Group>
                </Col>
                
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>&nbsp;</Form.Label>
                    <div>
                      <Button 
                        variant="outline-secondary"
                        onClick={resetFilters}
                        className="w-100"
                      >
                        Limpar Filtros
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        <div className="dashboard-section-header">
          <h2 className="dashboard-section-title">
            <FaIdCard className="dashboard-section-icon" />
            Lista de Pessoas
          </h2>
          <p className="dashboard-section-subtitle">
            Total de {filteredPessoas.length} pessoa(s) encontrada(s)
          </p>
        </div>

        {/* Tabela de pessoas */}
        <Card className="modern-table-card">
          <Card.Body className="modern-card-body">
            {loading ? (
              <div className="text-center py-5">
                <FaSpinner className="fa-spin mb-3" size={36} />
                <p>Carregando dados...</p>
              </div>
            ) : (
              <div className="modern-table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th>Documento</th>
                      <th>Expositor</th>
                      <th>Empresa Montadora</th>
                      <th>Contato</th>
                      <th>Período</th>
                      <th>Entrada</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPessoas.length > 0 ? (
                      filteredPessoas.map(pessoa => (
                        <tr key={`${pessoa.tipo}-${pessoa.id}`}>
                          <td className="font-weight-bold">
                            <div>
                              <div>{pessoa.nome}</div>
                              {pessoa.cargo && (
                                <small className="text-muted">
                                  <FaUserTie className="me-1" />
                                  {pessoa.cargo}
                                </small>
                              )}
                            </div>
                          </td>
                          <td>{renderTipoBadge(pessoa.tipo, pessoa.tipoCredencial)}</td>
                          <td>
                            <div className="font-weight-bold">
                              {pessoa.cpf}
                            </div>
                          </td>
                          <td>
                            <div className="font-weight-bold">
                              {pessoa.expositorAssociado || '-'}
                            </div>
                          </td>
                          <td>
                            {pessoa.tipo === 'montador' ? (
                              <div>
                                <FaBuilding className="me-1 text-muted" />
                                {pessoa.empresaMontadora}
                              </div>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            <div>
                              <div>{pessoa.telefone || '-'}</div>
                              <small className="text-muted">{pessoa.email || '-'}</small>
                            </div>
                          </td>
                          <td>
                            <div>
                              {pessoa.dataInicio && pessoa.dataFim ? (
                                <small className="text-muted">
                                  <FaCalendarAlt className="me-1" />
                                  {formatDate(pessoa.dataInicio)} a {formatDate(pessoa.dataFim)}
                                </small>
                              ) : (
                                <span className="text-muted">-</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div>
                              {renderStatusEntradaBadge(pessoa.statusEntrada)}
                              {pessoa.dataUltimaEntrada && (
                                <small className="text-muted d-block mt-1">
                                  <FaCalendarAlt className="me-1" />
                                  {formatDateTime(pessoa.dataUltimaEntrada)}
                                </small>
                              )}
                              {pessoa.pulseira && (
                                <small className="d-block mt-1">
                                  <Badge bg="info" pill>
                                    Pulseira: {pessoa.pulseira}
                                  </Badge>
                                </small>
                              )}
                            </div>
                          </td>
                          <td>{renderStatusBadge(pessoa.status)}</td>
                          <td>
                            <div className="action-buttons">
                              <Button 
                                variant="outline-danger" 
                                size="sm"
                                onClick={() => handleViewDetails(pessoa)}
                                className="me-1"
                                title="Editar"
                              >
                                <FaEdit />
                              </Button>
                              <Button 
                                variant="outline-primary" 
                                size="sm"
                                onClick={() => handleViewDetails(pessoa)}
                                className="me-1"
                                title="Ver Detalhes"
                              >
                                <FaEye />
                              </Button>
                              <Button 
                                variant="outline-secondary" 
                                size="sm"
                                onClick={() => handleViewHistory(pessoa)}
                                title="Histórico de Acesso"
                              >
                                <FaHistory />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          <div className="d-flex flex-column align-items-center">
                            <FaInfoCircle size={40} className="text-muted mb-2" />
                            <p className="text-muted mb-0">Nenhuma pessoa encontrada com os filtros aplicados</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Resumo estatístico */}
        {filteredPessoas.length > 0 && (
          <Row className="mt-4">
            <Col md={2}>
              <Card className="modern-table-card">
                <Card.Body className="text-center">
                  <h4 className="text-primary">{filteredPessoas.filter(p => p.tipo === 'credencial').length}</h4>
                  <p className="mb-0 text-muted">Credenciais</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="modern-table-card">
                <Card.Body className="text-center">
                  <h4 className="text-secondary">{filteredPessoas.filter(p => p.tipo === 'montador').length}</h4>
                  <p className="mb-0 text-muted">Montadores</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="modern-table-card">
                <Card.Body className="text-center">
                  <h4 className="text-success">{filteredPessoas.filter(p => p.status === 'ativo').length}</h4>
                  <p className="mb-0 text-muted">Ativos</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="modern-table-card">
                <Card.Body className="text-center">
                  <h4 className="text-danger">{filteredPessoas.filter(p => p.status === 'inativo').length}</h4>
                  <p className="mb-0 text-muted">Inativos</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="modern-table-card">
                <Card.Body className="text-center">
                  <h4 className="text-success">{filteredPessoas.filter(p => p.statusEntrada === 'registrado').length}</h4>
                  <p className="mb-0 text-muted">Registrados</p>
                </Card.Body>
              </Card>
            </Col>
            <Col md={2}>
              <Card className="modern-table-card">
                <Card.Body className="text-center">
                  <h4 className="text-warning">{filteredPessoas.filter(p => p.statusEntrada === 'pendente').length}</h4>
                  <p className="mb-0 text-muted">Pendentes</p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </div>

      {/* Modal de Detalhes */}
      <Modal 
        show={showDetailsModal} 
        onHide={handleCloseDetails} 
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaUser className="me-2" />
            Detalhes da Pessoa
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPerson && (
            <Row>
              <Col md={12}>
                <div className="text-center mb-4">
                  <h4>{selectedPerson.nome}</h4>
                  <div className="mb-2">
                    {renderTipoBadge(selectedPerson.tipo, selectedPerson.tipoCredencial)}
                  </div>
                  <div className="mb-2">
                    {renderStatusBadge(selectedPerson.status)}
                  </div>
                  <div>
                    {renderStatusEntradaBadge(selectedPerson.statusEntrada)}
                  </div>
                  {selectedPerson.pulseira && (
                    <div className="mt-2">
                      <Badge bg="info" pill>
                        Pulseira: {selectedPerson.pulseira}
                      </Badge>
                    </div>
                  )}
                </div>
                
                <h5 className="mb-3">Informações Pessoais</h5>
                
                <Row className="mb-3">
                  <Col sm={6}>
                    <strong>Documento:</strong><br />
                    <span>{selectedPerson.cpf}</span>
                  </Col>
                  <Col sm={6}>
                    <strong>Tipo:</strong><br />
                    <span className="text-capitalize">{selectedPerson.tipo} {selectedPerson.tipoCredencial ? `(${selectedPerson.tipoCredencial})` : ''}</span>
                  </Col>
                </Row>
                
                <Row className="mb-3">
                  <Col sm={6}>
                    <strong><FaPhone className="me-1" />Telefone:</strong><br />
                    <span>{selectedPerson.telefone || 'Não informado'}</span>
                  </Col>
                  <Col sm={6}>
                    <strong><FaEnvelope className="me-1" />E-mail:</strong><br />
                    <span>{selectedPerson.email || 'Não informado'}</span>
                  </Col>
                </Row>
                
                <Row className="mb-3">
                  <Col sm={6}>
                    <strong>Cargo:</strong><br />
                    <span>{selectedPerson.cargo || 'Não informado'}</span>
                  </Col>
                  <Col sm={6}>
                    {selectedPerson.tipo === 'montador' && (
                      <>
                        <strong>Empresa Montadora:</strong><br />
                        <span>{selectedPerson.empresaMontadora || 'Não informado'}</span>
                      </>
                    )}
                    {selectedPerson.tipo === 'credencial' && (
                      <>
                        <strong>Empresa:</strong><br />
                        <span>{selectedPerson.empresa || 'Não informado'}</span>
                      </>
                    )}
                  </Col>
                </Row>
                
                <h5 className="mb-3 mt-4">Informações do Evento</h5>
                
                <Row className="mb-3">
                  <Col sm={12}>
                    <strong>Expositor Associado:</strong><br />
                    <span>{selectedPerson.expositorAssociado || 'Não informado'}</span>
                  </Col>
                </Row>
                
                {selectedPerson.dataInicio && selectedPerson.dataFim && (
                  <Row className="mb-3">
                    <Col sm={6}>
                      <strong>Período de Trabalho:</strong><br />
                      <span>
                        <FaCalendarAlt className="me-1" />
                        {formatDate(selectedPerson.dataInicio)} a {formatDate(selectedPerson.dataFim)}
                      </span>
                    </Col>
                    {selectedPerson.horarioInicio && selectedPerson.horarioFim && (
                      <Col sm={6}>
                        <strong>Horário:</strong><br />
                        <span>
                          <FaClock className="me-1" />
                          {selectedPerson.horarioInicio} às {selectedPerson.horarioFim}
                        </span>
                      </Col>
                    )}
                  </Row>
                )}
                
                <h5 className="mb-3 mt-4">Status de Acesso</h5>
                
                <Row className="mb-3">
                  <Col sm={6}>
                    <strong>Status de Entrada:</strong><br />
                    <span>
                      {selectedPerson.statusEntrada === 'registrado' ? (
                        <span className="text-success">
                          <FaCheckCircle className="me-1" />
                          Entrada Registrada
                        </span>
                      ) : (
                        <span className="text-warning">
                          <FaTimesCircle className="me-1" />
                          Entrada Pendente
                        </span>
                      )}
                    </span>
                  </Col>
                  <Col sm={6}>
                    <strong>Última Entrada:</strong><br />
                    <span>
                      {selectedPerson.dataUltimaEntrada ? (
                        <>
                          <FaClock className="me-1" />
                          {formatDateTime(selectedPerson.dataUltimaEntrada)}
                        </>
                      ) : (
                        <span className="text-muted">Nenhuma entrada registrada</span>
                      )}
                    </span>
                  </Col>
                </Row>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseDetails}>
            Fechar
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              handleCloseDetails();
              handleViewHistory(selectedPerson);
            }}
          >
            <FaHistory className="me-1" />
            Ver Histórico Completo
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal de Histórico */}
      <Modal 
        show={showHistoryModal} 
        onHide={handleCloseHistory} 
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaHistory className="me-2" />
            Histórico de Acesso
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPerson && (
            <>
              <div className="d-flex align-items-center mb-4">
                <div>
                  <h4 className="mb-0">{selectedPerson.nome}</h4>
                  <p className="text-muted mb-0">
                    {selectedPerson.tipo === 'montador' ? selectedPerson.empresaMontadora : selectedPerson.empresa}
                    {selectedPerson.cargo && ` - ${selectedPerson.cargo}`}
                  </p>
                  <div className="mt-1">
                    {renderTipoBadge(selectedPerson.tipo, selectedPerson.tipoCredencial)}
                    <Badge bg={selectedPerson.status === 'ativo' ? 'success' : 'danger'} className="ms-2">
                      {selectedPerson.status === 'ativo' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-4">
                  <FaSpinner className="fa-spin mb-3" size={30} />
                  <p>Carregando histórico...</p>
                </div>
              ) : accessHistory.length > 0 ? (
                <div className="modern-table-container">
                  <table className="modern-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>Tipo</th>
                        <th>Pulseira</th>
                        <th>Status</th>
                        <th>Registrado por</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accessHistory.map(access => (
                        <tr key={access.id}>
                          <td>
                            <div>
                              <div><strong>{formatDate(access.data_entrada)}</strong></div>
                              <small className="text-muted">
                                {new Date(access.data_entrada).toLocaleTimeString()}
                              </small>
                            </div>
                          </td>
                          <td>
                            <Badge bg={access.data_saida ? 'warning' : 'success'} className="modern-badge">
                              {access.data_saida ? 'Saída' : 'Entrada'}
                            </Badge>
                            {access.data_saida && (
                              <div className="mt-1">
                                <small className="text-muted">
                                  {formatDateTime(access.data_saida)}
                                </small>
                              </div>
                            )}
                          </td>
                          <td>
                            {access.pulseira_numero ? (
                              <Badge bg="info" className="modern-badge">
                                {access.pulseira_numero}
                              </Badge>
                            ) : (
                              <span className="text-muted">Não registrada</span>
                            )}
                          </td>
                          <td>
                            <Badge bg={access.status_entrada === 'normal' ? 'success' : 'warning'} className="modern-badge">
                              {access.status_entrada || 'Normal'}
                            </Badge>
                          </td>
                          <td>{access.registrado_por || 'Sistema'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <Alert variant="info">
                  <FaInfoCircle className="me-2" />
                  Nenhum registro de acesso encontrado para esta pessoa.
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseHistory}>
            Fechar
          </Button>

        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Acessos;